import {ChainMapper, Mapper, MAPPER_NAMES, MAPPER_VALUES, MapperKind} from '@/api/models/Feeds.ts';
import Mappers, {ChangeFn} from '@/pages/feeds/Editor/Mapper/Mappers/index.tsx';
import {createEmptyMapperFromKind} from '@/pages/feeds/Editor/Mapper/MappingDrawer.tsx';
import {Button, Card, H3, MenuItem} from '@blueprintjs/core';
import {ItemRenderer, Select} from '@blueprintjs/select';
import {ReactElement, useCallback} from 'react';

interface Props {
	mapper: ChainMapper,
	onChange: ChangeFn,
}

export default function ChainMapperEditor({mapper, onChange}: Props): ReactElement {
	const onChildChange: ChildChangeFn = useCallback((index, child) => {
		const children = [...mapper.mappers];
		children[index] = child;

		onChange({
			...mapper,
			mappers: children,
		});
	}, [mapper, onChange]);

	const onChildDelete: DeleteFn = useCallback(index => {
		const children = mapper.mappers.filter((_value, i) => i !== index);

		onChange({
			...mapper,
			mappers: children,
		});
	}, [mapper, onChange])

	const onAddChildSelect = useCallback((newChild: Mapper) => {
		const children = [...mapper.mappers, newChild];

		onChange({
			...mapper,
			mappers: children,
		});
	}, [onChange, mapper]);

	return (
		<>
			<AddChildSelect onSelect={onAddChildSelect} />

			{mapper.mappers.map((child, index) => (
				<Child key={index} index={index} mapper={child} onChange={onChildChange} onDelete={onChildDelete} />
			))}
		</>
	);
}

type ChildChangeFn = (index: number, child: Mapper) => void;
type DeleteFn = (index: number) => void;

interface ChildProps {
	index: number,
	mapper: Mapper,
	onChange: ChildChangeFn,
	onDelete: DeleteFn,
}

function Child({index, mapper, onChange, onDelete}: ChildProps): ReactElement {
	const onMapperChange: ChangeFn = useCallback(mapper => {
		onChange(index, mapper);
	}, [onChange, index]);

	const onMapperDelete = useCallback(() => {
		onDelete(index);
	}, [index, onDelete])

	return (
		<>
			<Mappers mapper={mapper} onChange={onMapperChange}>
				<div style={{display: 'flex', justifyContent: 'end', marginTop: 10}}>
					<Button intent="danger" icon="delete" text="Delete" onClick={onMapperDelete} />
				</div>
			</Mappers>
		</>
	);
}

interface AddChildSelectProps {
	onSelect: (newChild: Mapper) => void,
}

function AddChildSelect({onSelect}: AddChildSelectProps): ReactElement {
	const renderItem: ItemRenderer<MapperKind> = useCallback((item, {handleFocus, handleClick}) => {
		return (
			<MenuItem
				key={item}
				text={MAPPER_NAMES[item]}
				onClick={handleClick}
				onFocus={handleFocus}
			/>
		);
	}, []);

	const onItemSelect = useCallback((kind: MapperKind) => {
		onSelect(createEmptyMapperFromKind(kind));
	}, [onSelect]);

	return (
		<Select
			items={MAPPER_VALUES}
			itemRenderer={renderItem}
			onItemSelect={onItemSelect}
			filterable={false}
			popoverProps={{
				placement: 'bottom-start',
			}}
			popoverTargetProps={{
				style: {display: 'inline-block'}
			}}
		>
			<Button icon="add" intent="primary" text="Add" endIcon="chevron-down" />
		</Select>
	);
}
